import { supabase } from "@/integrations/supabase/client";
import type { Notification, NotificationPreference } from "./types";

export async function fetchNotifications(userId: string, limit = 10) {
  const { data, error } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data as Notification[];
}

export async function fetchUnreadCount(userId: string) {
  const { count, error } = await supabase
    .from("notifications")
    .select("*", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("is_read", false);

  if (error) throw error;
  return count || 0;
}

export async function markAsRead(notificationId: string) {
  const { error } = await supabase
    .from("notifications")
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq("id", notificationId);

  if (error) throw error;
}

export async function markAllAsRead(userId: string) {
  const { error } = await supabase
    .from("notifications")
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq("user_id", userId)
    .is("is_read", false);

  if (error) throw error;
}

export async function createNotification(notification: {
  user_id: string;
  type: string;
  title: string;
  message?: string;
  data?: Record<string, any>;
  priority?: number;
}) {
  const { data, error } = await supabase
    .from("notifications")
    .insert({
      user_id: notification.user_id,
      type: notification.type,
      title: notification.title,
      message: notification.message || null,
      data: notification.data || {},
      priority: notification.priority || 0,
      is_read: false,
      included_in_digest: true,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Notification;
}

/**
 * Create a notification, or update an existing unread one of the same type
 * for the same user + dedupe key created within `windowMinutes`. Useful when
 * the same event can fire repeatedly in a short window (e.g. multiple gang
 * sheet saves) and we don't want to spam the bell.
 */
export async function createOrUpdateNotification(
  notification: {
    user_id: string;
    type: string;
    title: string;
    message?: string;
    data?: Record<string, any>;
    priority?: number;
  },
  options: { dedupeKey: string; dedupeValue: string; windowMinutes?: number },
) {
  const windowMinutes = options.windowMinutes ?? 10;
  const since = new Date(Date.now() - windowMinutes * 60 * 1000).toISOString();

  const { data: existing } = await supabase
    .from("notifications")
    .select("*")
    .eq("user_id", notification.user_id)
    .eq("type", notification.type)
    .eq("is_read", false)
    .gte("created_at", since)
    .contains("data", { [options.dedupeKey]: options.dedupeValue })
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existing) {
    const prevCount = Number((existing.data as any)?.update_count ?? 1) || 1;
    const update_count = prevCount + 1;
    const mergedData = {
      ...(existing.data as Record<string, any>),
      ...(notification.data || {}),
      update_count,
      last_updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from("notifications")
      .update({
        title: notification.title,
        message: notification.message
          ? `${notification.message} (${update_count} updates)`
          : existing.message,
        data: mergedData,
        priority: notification.priority ?? existing.priority ?? 0,
        updated_at: new Date().toISOString(),
      })
      .eq("id", existing.id)
      .select()
      .single();
    if (error) throw error;
    return data as Notification;
  }

  return createNotification(notification);
}

export async function fetchNotificationPreferences(userId: string) {
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("*")
    .eq("user_id", userId)
    .single();

  if (error && error.code !== "PGRST116") throw error;
  return data as NotificationPreference | null;
}

export async function upsertNotificationPreferences(
  userId: string,
  preferences: Partial<NotificationPreference>,
) {
  const { data, error } = await supabase
    .from("notification_preferences")
    .upsert({
      user_id: userId,
      ...preferences,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data as NotificationPreference;
}

export async function shouldCreateNotification(
  userId: string,
  eventType:
    | "job_created"
    | "job_print_started"
    | "job_ready"
    | "job_on_hold"
    | "gang_sheet_updated",
  channel: "inapp" | "email",
): Promise<boolean> {
  try {
    const prefs = await fetchNotificationPreferences(userId);
    if (!prefs) return true; // Default to true if no preferences set

    // Check the specific flag for this event/channel combination
    if (channel === "inapp") {
      switch (eventType) {
        case "job_created":
          return prefs.inapp_job_created ?? true;
        case "job_print_started":
          return prefs.inapp_job_print_started ?? true;
        case "job_ready":
          return prefs.inapp_job_ready ?? true;
        case "job_on_hold":
          return prefs.inapp_job_on_hold ?? true;
        case "gang_sheet_updated":
          return prefs.inapp_gang_sheet_updated ?? true;
        default:
          return true;
      }
    } else {
      switch (eventType) {
        case "job_created":
          return prefs.email_job_created ?? true;
        case "job_print_started":
          return prefs.email_job_print_started ?? true;
        case "job_ready":
          return prefs.email_job_ready ?? true;
        case "job_on_hold":
          return prefs.email_job_on_hold ?? true;
        case "gang_sheet_updated":
          return prefs.email_gang_sheet_updated ?? true;
        default:
          return true;
      }
    }
  } catch {
    return true; // Default to true on error
  }
}

export async function createNotificationIfEnabled(
  userId: string,
  eventType:
    | "job_created"
    | "job_print_started"
    | "job_ready"
    | "job_on_hold"
    | "gang_sheet_updated",
  notification: {
    type: string;
    title: string;
    message?: string;
    data?: Record<string, any>;
    priority?: number;
  },
) {
  const shouldCreate = await shouldCreateNotification(userId, eventType, "inapp");
  if (shouldCreate) {
    return createNotification({
      user_id: userId,
      ...notification,
    });
  }
  return null;
}
